import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Tag, TextArea, TextInput } from '@carbon/react';
import { formatVnd, GAME_GROUP_LABEL, type GameGroup } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { MoneyInput } from '../components/MoneyInput';

// Game safety (backend/lib/an_toan_game.js, P1-D22): automatic stop per game and the per-round
// house loss cap. The game servers enforce both; this page only writes the thresholds and reopens.
type TuDung = { phut?: number; thangRong?: number; rtp?: number; cuocToiThieu?: number; moLaiTu?: string };
type Safety = { tuDung?: TuDung; tranLoVan?: number; tranLoQuy?: number };
type Stats = { phut?: number; cuoc?: number; tra?: number; thangRong?: number; rtp?: number; rtpThat?: number | null };
type Stop = { id: number; game: string; name: string; at: string; reason: string; stats: Stats | null; reopenedAt: string | null };
type Row = { id: string; name: string; group: GameGroup; cap?: 'fixed' | 'pool'; safety: Safety | null; stop: Stop | null };
type Data = { games: Row[]; history: Stop[]; meta: { version: number; source: string; at: string | null }; pending: { id: string }[] };
type Draft = { phut: string; thangRong: string; rtp: string; cuocToiThieu: string; tranLoVan: string; tranLoQuy: string };

const s = (n: number | undefined) => (n ? String(n) : '');
const toDraft = (x: Safety | null): Draft => ({
  phut: s(x?.tuDung?.phut), thangRong: s(x?.tuDung?.thangRong), rtp: s(x?.tuDung?.rtp), cuocToiThieu: s(x?.tuDung?.cuocToiThieu),
  tranLoVan: s(x?.tranLoVan), tranLoQuy: x?.tranLoQuy ? String(Math.round(x.tranLoQuy * 1000) / 10) : '',
});
const toSafety = (d: Draft) => {
  const n = (v: string) => (v ? Number(v) : undefined);
  return {
    tuDung: { phut: n(d.phut), thangRong: n(d.thangRong), rtp: n(d.rtp), cuocToiThieu: n(d.cuocToiThieu) },
    tranLoVan: n(d.tranLoVan), tranLoQuy: d.tranLoQuy ? Number(d.tranLoQuy) / 100 : undefined,
  };
};
const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);
const digits = (v: string) => v.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 13);
const decimal = (v: string, max: number) => {
  const c = v.replace(',', '.').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 8);
  return c && Number(c) > max ? String(max) : c;
};

function describeStop(t: Stop) {
  const st = t.stats ?? {};
  if (t.reason === 'thang_rong') return `Người chơi thắng ròng ${formatVnd((st.tra ?? 0) - (st.cuoc ?? 0))} trong ${st.phut} phút (ngưỡng ${formatVnd(st.thangRong ?? 0)})`;
  return `Tỷ lệ trả ${st.rtpThat != null ? st.rtpThat + '%' : 'vô hạn (có tiền trả, không có cược)'} trong ${st.phut} phút trên ${formatVnd(st.cuoc ?? 0)} cược (ngưỡng ${st.rtp}%)`;
}
function describe(d: Draft, cap?: 'fixed' | 'pool') {
  const p = [];
  if (d.thangRong) p.push('thắng ròng > ' + formatVnd(Number(d.thangRong)));
  if (d.rtp) p.push('trả > ' + d.rtp + '%' + (d.cuocToiThieu ? ' (từ ' + formatVnd(Number(d.cuocToiThieu)) + ' cược)' : ''));
  const stop = p.length ? 'Tự dừng khi ' + p.join(' hoặc ') + ' trong ' + (d.phut || '10') + ' phút' : 'Không tự dừng';
  const c = [];
  if (cap && d.tranLoVan) c.push(formatVnd(Number(d.tranLoVan)));
  if (cap === 'pool' && d.tranLoQuy) c.push(d.tranLoQuy + '% quỹ');
  return stop + (c.length ? '; nhà cái thua tối đa ' + c.join(' và ') + ' một ván' : '');
}
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function ReopenModal({ row, version, onClose }: { row: Row; version: number; onClose: (c: Command | null) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>('/api/admin/games/safety/reopen', { id: row.id, expect: version, reason }),
    onSuccess: (r) => onClose(r.command),
  });
  return (
    <Modal open modalHeading={'Mở lại ' + row.name} primaryButtonText="Mở lại nhận cược" secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending || reason.trim().length < 3} onRequestClose={() => onClose(null)} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        {row.stop ? <p>{describeStop(row.stop)}. Dừng lúc <When v={row.stop.at} />.</p> : null}
        <p>Game nhận cược lại từ lượt sau và bắt đầu đếm lại từ đầu. Hãy chắc đã kiểm tra các ván thắng lớn trong sổ giao dịch.</p>
        <TextArea id="reopen-reason" labelText="Lý do" rows={2} maxCount={300} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

export function GameSafetyPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['game-safety'], queryFn: () => api.get<Data>('/api/admin/games/safety'), refetchInterval: 15000 });
  const [draft, setDraft] = useState<Map<string, Draft>>(new Map());
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [reopen, setReopen] = useState<Row | null>(null);
  const [result, setResult] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/safety', {
      changes: Object.fromEntries([...draft].map(([id, d]) => [id, toSafety(d)])),
      expect: base ?? q.data!.meta.version,
    }),
    onSuccess: (r) => { setSaving(false); setDraft(new Map()); setBase(null); setResult(r.command); qc.invalidateQueries({ queryKey: ['game-safety'] }); },
    onError: (e) => { if (isStale(e)) setSaving(false); },
  });
  const stale = isStale(m.error);
  const discard = () => { setDraft(new Map()); setBase(null); m.reset(); qc.invalidateQueries({ queryKey: ['game-safety'] }); };

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { games, history, meta, pending } = q.data;
  const byId = new Map(games.map((g) => [g.id, g]));
  const editable = can('games.config');
  const eff = (g: Row) => draft.get(g.id) ?? toDraft(g.safety);
  const edit = (g: Row, d: Draft) => {
    const n = new Map(draft);
    if (same(d, toDraft(g.safety))) n.delete(g.id); else n.set(g.id, d);
    setDraft(n);
    setBase(n.size ? (base ?? meta.version) : null);
  };
  const stopped = games.filter((g) => g.stop);
  const field = (g: Row, d: Draft, k: keyof Draft, label: string, kind: 'money' | 'int' | 'pct', disabled = false) => kind === 'money' ? (
    <MoneyInput id={k + '-' + g.id} size="sm" hideLabel labelText={label + ' ' + g.name} className="money-input money-input--wide" maxDigits={13}
      placeholder="tắt" disabled={!editable || disabled} value={d[k]} onChange={(v) => edit(g, { ...d, [k]: v })} />
  ) : (
    <TextInput id={k + '-' + g.id} size="sm" hideLabel labelText={label + ' ' + g.name} inputMode="decimal" className="money-input money-input--short"
      placeholder={k === 'phut' ? '10' : 'tắt'} disabled={!editable || disabled} value={d[k]}
      onChange={(e) => edit(g, { ...d, [k]: kind === 'pct' ? decimal(e.target.value, k === 'tranLoQuy' ? 100 : 100000) : digits(e.target.value).slice(0, 4) })} />
  );

  return (
    <div className="page page--with-bar">
      <PageHeader title="An toàn game"
        lead="Máy chủ game tự đếm tiền cược và tiền trả của người chơi thật trong một khoảng thời gian trượt. Vượt ngưỡng thì game dừng nhận cược ngay (như tắt game), ván đang mở vẫn chốt bình thường và founder nhận tin Telegram. Ván đặt kết quả không tính. Để trống là tắt." />
      <div className="row health">
        <span>Cài đặt: {meta.source === 'default' ? <span className="muted">chưa đặt từ CMS</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
        {pending.length ? <span className="muted">{pending.length} lệnh đang gửi</span> : null}
      </div>
      {stopped.length ? (
        <InlineNotification kind="error" hideCloseButton title={`${stopped.length} game đang tự dừng`}
          subtitle={stopped.map((g) => g.name).join(', ')} />
      ) : null}
      {result ? (
        <InlineNotification kind={result.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => setResult(null)}
          title={result.status === 'SUCCEEDED' ? 'Hệ A đã nhận cài đặt mới' : 'Lệnh chưa xong'}
          subtitle={result.status === 'SUCCEEDED' ? '' : (result.lastError && HE_A_ERRORS[result.lastError]) || result.lastError || result.status} />
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton title="Cài đặt vừa được người khác đổi"
          subtitle={`Bản bạn đang sửa dựa trên phiên bản ${base ?? '?'}, trên máy chủ đã là phiên bản ${meta.version}. Chưa lưu gì.`}
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={discard} />
      ) : null}
      <div className="table-scroll">
      <table className="plain vis">
        <thead>
          <tr>
            <th>Game</th><th>Trạng thái</th><th className="r">Khoảng (phút)</th><th className="r">Thắng ròng tối đa</th>
            <th className="r">Tỷ lệ trả tối đa (%)</th><th className="r">Cược tối thiểu xét tỷ lệ</th>
            <th className="r">Nhà cái thua tối đa một ván</th><th className="r">Theo quỹ (%)</th>
          </tr>
        </thead>
        <tbody>
          {games.map((g) => {
            const d = eff(g);
            return (
              <tr key={g.id} className={draft.has(g.id) ? 'is-changed' : undefined}>
                <td className="is-title"><div className="vis__name">{g.name}</div><div className="muted num">{g.id} · {GAME_GROUP_LABEL[g.group]}</div></td>
                <td data-label="Trạng thái">
                  {g.stop ? (
                    <div className="stack">
                      <Tag size="sm" type="red">Tự dừng</Tag>
                      <span className="vis__note">{describeStop(g.stop)}, <When v={g.stop.at} /></span>
                      {editable ? <Button kind="danger--tertiary" size="sm" onClick={() => setReopen(g)}>Mở lại</Button> : null}
                    </div>
                  ) : <Tag size="sm" type="green">Nhận cược</Tag>}
                </td>
                <td className="r" data-label="Khoảng (phút)">{field(g, d, 'phut', 'Khoảng phút', 'int')}</td>
                <td className="r" data-label="Thắng ròng tối đa">{field(g, d, 'thangRong', 'Thắng ròng tối đa', 'money')}</td>
                <td className="r" data-label="Tỷ lệ trả tối đa">{field(g, d, 'rtp', 'Tỷ lệ trả tối đa', 'pct')}</td>
                <td className="r" data-label="Cược tối thiểu xét tỷ lệ">{field(g, d, 'cuocToiThieu', 'Cược tối thiểu xét tỷ lệ', 'money')}</td>
                <td className="r" data-label="Thua tối đa một ván">{g.cap ? field(g, d, 'tranLoVan', 'Nhà cái thua tối đa một ván', 'money') : <span className="muted">không trả theo cửa</span>}</td>
                <td className="r" data-label="Theo quỹ">{g.cap === 'pool' ? field(g, d, 'tranLoQuy', 'Phần trăm quỹ', 'pct') : <span className="muted">—</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <p className="note">Trần thua một ván: cược làm số tiền nhà cái có thể thua ở kết quả tệ nhất của ván (chỉ tính người chơi thật) vượt trần thì bị từ chối ngay lúc đặt. Theo quỹ = phần trăm số dư quỹ điều khiển của bàn; không đọc được quỹ thì từ chối cược.</p>

      <section className="panel">
      <div className="panel__head"><h2>Lịch sử tự dừng</h2></div>
      {history.length ? (
        <table className="plain">
          <thead><tr><th>Lúc</th><th>Game</th><th>Lý do</th><th>Mở lại</th></tr></thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td data-label="Lúc"><When v={h.at} /></td>
                <td data-label="Game">{h.name}</td>
                <td data-label="Lý do">{describeStop(h)}{h.stats?.cuoc != null ? <span className="muted"> · cược <Money v={h.stats.cuoc} />, trả <Money v={h.stats.tra ?? 0} /></span> : null}</td>
                <td data-label="Mở lại">{h.reopenedAt ? <When v={h.reopenedAt} /> : byId.get(h.game)?.stop?.id === h.id ? <Tag size="sm" type="red">đang dừng</Tag> : <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="muted">Chưa game nào tự dừng.</p>}
      </section>

      {draft.size ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{draft.size}</span> game đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={discard}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={stale} onClick={() => setSaving(true)}>Lưu</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Lưu cài đặt an toàn ${draft.size} game`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
            <tbody>
              {[...draft].map(([id, d]) => {
                const g = byId.get(id);
                return <tr key={id}><td className="is-title">{g?.name ?? id}</td><td data-label="Trước">{describe(toDraft(g?.safety ?? null), g?.cap)}</td><td data-label="Sau">{describe(d, g?.cap)}</td></tr>;
              })}
            </tbody>
          </table>
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
      {reopen ? (
        <ReopenModal row={reopen} version={meta.version} onClose={(c) => { setReopen(null); if (c) { setResult(c); qc.invalidateQueries({ queryKey: ['game-safety'] }); } }} />
      ) : null}
    </div>
  );
}
